import { DEFAULT_CURRENCY_FORMATTER } from "@redotech/money/util";
import {
  ProductNotificationGroupBy,
  VariantStockStatus,
  type BackInStockProduct,
} from "@redotech/redo-customer-common/customer-product-notification/customer-product-notification";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import * as styles from "./bis.module.css";

export const ProductListItem = memo(function ProductListItem({
  product,
  isSelected,
  onClick,
}: {
  product: BackInStockProduct;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      bgColor={isSelected ? "secondary" : "primary"}
      borderColor="secondary"
      borderRadius="xl"
      borderWidth="1px"
      className={isSelected ? styles.selectedCard : undefined}
      cursor="pointer"
      dir="column"
      gap="sm"
      onClick={onClick}
      p="lg"
    >
      <Flex align="flex-start" gap="md">
        <Flex
          align="center"
          bgColor="tertiary"
          borderRadius="lg"
          className={styles.imageContainer}
          justify="center"
        >
          {product.imageUrl ? (
            <img
              alt={product.name}
              src={product.imageUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "var(--border-radius-lg)",
              }}
            />
          ) : (
            <Text fontSize="xs" textColor="secondary">
              [img]
            </Text>
          )}
        </Flex>
        <Flex dir="column" flex="1" gap="xs">
          <Text fontSize="sm" fontWeight="medium" textColor="primary">
            {product.name}
          </Text>
          <ProductListItemSubtext product={product} />
          <ProductListItemRevenue product={product} />
        </Flex>
      </Flex>
    </Flex>
  );
});

const ProductListItemSubtext = memo(function ProductListItemSubtext({
  product,
}: {
  product: BackInStockProduct;
}) {
  if (product.groupBy === ProductNotificationGroupBy.PRODUCT) {
    return (
      <Text fontSize="xs" textColor="secondary">
        {product.waitingCount} waiting &bull; {product.inStockCount} in stock
        &bull; {product.outOfStockCount} out of stock
      </Text>
    );
  }
  return (
    <Flex align="center" gap="sm">
      <Text fontSize="xs" textColor="secondary">
        {product.waitingCount} waiting
      </Text>
      {product.notifiedCount > 0 && (
        <Text fontSize="xs" textColor="secondary">
          &bull; {product.notifiedCount} notified
        </Text>
      )}
      {product.status === VariantStockStatus.OUT_OF_STOCK && (
        <RedoBadge text="Out of stock" />
      )}
    </Flex>
  );
});

function ProductListItemRevenue({ product }: { product: BackInStockProduct }) {
  switch (product.groupBy) {
    case ProductNotificationGroupBy.PRODUCT:
      return (
        <Text fontSize="sm" fontWeight="medium" textColor="success">
          {DEFAULT_CURRENCY_FORMATTER.format(product.potentialRevenue)}{" "}
          potential
        </Text>
      );
    case ProductNotificationGroupBy.VARIANT:
      return (
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          {DEFAULT_CURRENCY_FORMATTER.format(product.potentialRevenue)}
        </Text>
      );
  }
}
